/**
 * InfinitSales - Agent Service
 * Service for agent CRUD operations via backend API
 * 
 * The backend proxies to InfinitAgents API using user's API key.
 * Agent sections are stored in user document for dynamic prompt building.
 */

import api from '../global/api'; // Axios instance with auth interceptor

// ============================================
// AGENT CRUD
// ============================================

/**
 * Create a new agent (direct, without onboarding flow)
 * @param {Object} agentData - Agent configuration
 * @returns {Promise<Object>} - Created agent
 */
export const createAgent = async (agentData) => {
  try {
    const response = await api.post('/agents', agentData);
    return response.data;
  } catch (error) {
    console.error('[AgentService] createAgent error:', error);
    throw error.response?.data || error;
  }
};

/**
 * Create agent from onboarding flow
 * This saves sections to user document for dynamic prompt building
 * @param {Object} onboardingData - Data from onboarding flow
 * @returns {Promise<Object>} - Created agent with sections
 */
export const createAgentFromOnboarding = async (onboardingData) => {
  try {
    console.log('[AgentService] Creating agent from onboarding:', onboardingData.name);
    
    const response = await api.post('/agents/onboarding', onboardingData);
    
    console.log('[AgentService] Agent created:', response.data);
    return response.data;
  } catch (error) {
    console.error('[AgentService] createAgentFromOnboarding error:', error);
    throw error.response?.data || error;
  }
};

/**
 * List all agents for the authenticated user
 * @param {Object} options - Query options
 * @param {string} options.search - Search term
 * @param {string} options.sort - Sort field (recent, name, etc)
 * @param {string} options.order - Sort order (ASC, DESC)
 * @returns {Promise<Array>} - List of agents
 */
export const listAgents = async (options = {}) => {
  try {
    const params = new URLSearchParams();
    if (options.search) params.append('search', options.search);
    if (options.sort) params.append('sort', options.sort);
    if (options.order) params.append('order', options.order);
    
    const queryString = params.toString();
    const url = queryString ? `/agents?${queryString}` : '/agents';
    
    const response = await api.get(url);
    return response.data?.data || [];
  } catch (error) {
    console.error('[AgentService] listAgents error:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get agent by ID
 * Also returns _sections if this is the active agent
 * @param {string} agentId - Agent ID
 * @returns {Promise<Object>} - Agent data
 */
export const getAgent = async (agentId) => {
  try {
    const response = await api.get(`/agents/${agentId}`);
    return response.data?.data || response.data;
  } catch (error) {
    console.error('[AgentService] getAgent error:', error);
    throw error.response?.data || error;
  }
};

/**
 * Update agent
 * @param {string} agentId - Agent ID
 * @param {Object} updateData - Fields to update
 * @returns {Promise<Object>} - Updated agent
 */
export const updateAgent = async (agentId, updateData) => {
  try {
    const response = await api.put(`/agents/${agentId}`, updateData);
    return response.data;
  } catch (error) {
    console.error('[AgentService] updateAgent error:', error);
    throw error.response?.data || error;
  }
};

/**
 * Delete agent
 * @param {string} agentId - Agent ID
 * @returns {Promise<Object>} - Success message
 */
export const deleteAgent = async (agentId) => {
  try {
    const response = await api.delete(`/agents/${agentId}`);
    return response.data;
  } catch (error) {
    console.error('[AgentService] deleteAgent error:', error);
    throw error.response?.data || error;
  }
};

// ============================================
// AGENT SECTIONS
// ============================================

/**
 * Get agent sections (from user document)
 * @param {string} agentId - Agent ID
 * @returns {Promise<Object>} - Agent sections
 */
export const getAgentSections = async (agentId) => {
  try {
    const response = await api.get(`/agents/${agentId}/sections`);
    return response.data?.data || response.data;
  } catch (error) {
    console.error('[AgentService] getAgentSections error:', error);
    throw error.response?.data || error;
  }
};

/**
 * Update agent sections (in user document)
 * @param {string} agentId - Agent ID
 * @param {Object} sections - Sections to update
 * @returns {Promise<Object>} - Updated sections
 */
export const updateAgentSections = async (agentId, sections) => {
  try {
    const response = await api.put(`/agents/${agentId}/sections`, sections);
    return response.data;
  } catch (error) {
    console.error('[AgentService] updateAgentSections error:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get agent sections history (current + up to 10 previous versions).
 * Backend mantém o histórico automaticamente em agent_sections.history.
 * @param {string} agentId
 * @returns {Promise<{ current: Object, current_saved_at: string|null, history: Array<{sections: Object, saved_at: string}> }>}
 */
export const getAgentHistory = async (agentId) => {
  try {
    const response = await api.get(`/agents/${agentId}/history`);
    return response.data?.data || { current: {}, current_saved_at: null, history: [] };
  } catch (error) {
    console.error('[AgentService] getAgentHistory error:', error);
    throw error.response?.data || error;
  }
};

/**
 * Restore a previous agent_sections version by index.
 * O backend valida campos obrigatórios; lança 422 se a versão estiver incompleta.
 * @param {string} agentId
 * @param {number} versionIndex
 * @returns {Promise<Object>} - Updated agent_sections
 */
export const restoreAgentVersion = async (agentId, versionIndex) => {
  try {
    const response = await api.post(`/agents/${agentId}/restore`, { version_index: versionIndex });
    return response.data?.data;
  } catch (error) {
    console.error('[AgentService] restoreAgentVersion error:', error);
    throw error.response?.data || error;
  }
};


// ============================================
// HELPER: Prepare onboarding data for API
// ============================================

/**
 * Prepare enhanced onboarding data for agent creation API
 * @param {Object} enhancedData - Data from agentEnhancerService
 * @param {string} businessCategory - Selected category ID
 * @returns {Object} - Data ready for createAgentFromOnboarding
 */
export const prepareOnboardingPayload = (enhancedData, businessCategory) => {
  return {
    name: enhancedData.agent_name || 'Meu Agente',
    description: `Agente para ${businessCategory}`,
    business_category: businessCategory,
    sections: {
      identity: enhancedData.sections?.identity || null,
      tone_of_voice: enhancedData.sections?.tone_of_voice || null,
      objective: enhancedData.sections?.objective || null,
      objective_details: enhancedData.sections?.objective_details || null,
      rules: enhancedData.sections?.rules || null,
      human_handoff: enhancedData.sections?.human_handoff || null,
      customization: enhancedData.sections?.customization || null,
      faq: enhancedData.faq || { items: [] }
    },
    // Optional overrides
    // Aspa dupla sobrando aqui até 2026-08-03 fazia TODO agente criado pelo
    // onboarding nascer com model='gpt-5-mini"', que não casa com nenhum
    // deployment (a lista válida da AzureOpenAI é ["gpt-4.1", "gpt-5-mini"]).
    // 2026-10-12: padrao de criacao = gpt-6-luna (recurso infinit-modelos-brs).
    model: 'gpt-6-luna',
    provider: 'AzureOpenAI',
    creativity: 0.7,
    max_tokens: 3000
  };
};

// ============================================
// CHAT STATE (Handoff Integration)
// ============================================

/**
 * Get chat state from InfinitAgents (active, handoff, ended)
 * @param {string} contactPhone - Contact phone number
 * @returns {Promise<Object>} - { state, state_metadata }
 */
export const getChatState = async (contactPhone) => {
  try {
    const response = await api.get(`/chats/${encodeURIComponent(contactPhone)}/state`);
    return response.data;
  } catch (error) {
    console.error('[AgentService] getChatState error:', error);
    return { state: 'active', state_metadata: {} };
  }
};

/**
 * Batch chat-state lookup. ONE request returns the handoff state for many
 * phones at once. Use this on inbox load instead of N getChatState calls.
 * @param {string[]} phones
 * @returns {Promise<Object>} - { [phone]: { state, state_metadata } } only for phones in handoff
 */
export const getChatStatesBatch = async (phones) => {
  try {
    const response = await api.post('/chats/state-batch', { phones: phones || [] });
    return response.data?.data || {};
  } catch (error) {
    console.error('[AgentService] getChatStatesBatch error:', error);
    return {};
  }
};

/**
 * Resume agent auto-response for a handoff chat
 * @param {string} contactPhone - Contact phone number
 * @returns {Promise<Object>} - Result
 */
export const resumeAgent = async (contactPhone) => {
  try {
    const response = await api.put(`/chats/${encodeURIComponent(contactPhone)}/resume-agent`);
    return response.data;
  } catch (error) {
    console.error('[AgentService] resumeAgent error:', error);
    throw error.response?.data || error;
  }
};

// ============================================
// EXPORT DEFAULT
// ============================================

export default {
  createAgent,
  createAgentFromOnboarding,
  listAgents,
  getAgent,
  updateAgent,
  deleteAgent,
  getAgentSections,
  updateAgentSections,
  getAgentHistory,
  restoreAgentVersion,
  prepareOnboardingPayload,
  getChatState,
  getChatStatesBatch,
  resumeAgent
};